import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';

/*
 * Constants.
 */

const PADDING = 40;

/*
 * Props.
 */

interface ImageWrapperProps {
  $shouldIgnoreImagePadding?: boolean;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  padding: ${PADDING}px;

  background-color: ${(p) => p.theme.greys.shade10};

  border-radius: 10px;

  overflow: hidden;

  display: grid;
  grid-template-columns: 1fr 50%;
  grid-template-rows: 20px 1fr 30px;
  grid-template-areas:
    'pill    image'
    'content image'
    'buttons image'
    'helper  .';
`;

const GridHelper = styled.div<{
  $area: 'pill' | 'image' | 'content' | 'buttons' | 'helper';
  $width?: number;
  $flex?: boolean;
  $align?: 'left' | 'right';
}>`
  ${(p) => (p.$flex ? 'display: flex;' : '')}
  position: relative;
  grid-area: ${(p) => p.$area};
  ${(p) => (p.$width ? `width: ${p.$width}px;` : '')}
`;

const StyledTitle = styled.h2`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  margin-bottom: 5px;
  margin-top: 12px;
`;
const StyledContent = styled.p`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  line-height: 20px;
  color: ${(p) => p.theme.greys.shade70};

  margin-top: 0px;
  margin-bottom: 20px;

  a {
    text-decoration: underline;
    color: inherit;
  }
`;

const ButtonWrapper = styled.div`
  width: fit-content;
  margin-right: 10px;
`;

const ImageWrapper = styled.div<ImageWrapperProps>`
  position: absolute;
  top: -${PADDING}px;
  right: ${(p) => (p.$shouldIgnoreImagePadding ? 0 : PADDING)}px;
  bottom: ${(p) => (p.$shouldIgnoreImagePadding ? 0 : PADDING)}px;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const PillWrapper = styled.div`
  grid-area: pill;

  display: flex;
  align-items: center;

  height: fit-content;
  padding-right: 20px;
`;

const StyledHelperTextDiv = styled.div`
  grid-area: helper;

  display: flex;
  align-items: center;

  height: fit-content;
  padding-top: 8px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

/*
 * Props.
 */

export interface UpsellCardProps {
  title: ReactNode;
  imageUrl: string;
  pill: ReactNode;
  buttons: [ReactNode] | [ReactNode, ReactNode];
  limitContentWidth?: boolean;
  shouldIgnoreImagePadding?: boolean;
  helperText?: ReactNode;
}

/*
 * Component.
 */

export const UpsellCard: FC<UpsellCardProps> = ({
  title,
  children,
  pill,
  buttons,
  imageUrl,
  limitContentWidth = true,
  shouldIgnoreImagePadding,
  helperText,
}) => (
  <StyledWrapperDiv>
    <PillWrapper>{pill}</PillWrapper>
    <GridHelper $area="content" $width={limitContentWidth ? 235 : undefined}>
      <StyledTitle>{title}</StyledTitle>
      <StyledContent>{children}</StyledContent>
    </GridHelper>
    <GridHelper $area="buttons" $flex>
      {renderButtons(buttons)}
    </GridHelper>
    <GridHelper $area="image">
      <ImageWrapper $shouldIgnoreImagePadding={shouldIgnoreImagePadding}>
        <img src={imageUrl} alt="" />
      </ImageWrapper>
    </GridHelper>
    <StyledHelperTextDiv>{helperText}</StyledHelperTextDiv>
  </StyledWrapperDiv>
);

/*
 * Helpers.
 */

function renderButtons(buttons: [ReactNode] | [ReactNode, ReactNode]) {
  const [left, right] = buttons;
  return (
    <>
      <ButtonWrapper>{left}</ButtonWrapper>
      {right && <ButtonWrapper>{right}</ButtonWrapper>}
    </>
  );
}
